import { EducatorPageChrome } from "@/pages/educators/page-chrome"
import { PageBody } from "@/components/ui/layout"

import { Inbox } from "lucide-react"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card"
import {
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  Empty,
} from "@/components/ui/empty"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { IndexProps, useIndexProps } from "./page-props"
import { useId } from "react"
import { Link } from "@/lib/page-rendering"

export default function Page() {
  const { live_classes, class_name } = useIndexProps()
  const { t } = useTranslation("class_membership_requests")

  return (
    <>
      <EducatorPageChrome records={{ "educators/classes/edit": class_name }} />
      <PageBody role="region">
        <ul className="flex flex-col gap-6">
          {live_classes.length === 0 ? (
            <Card>
              <CardContent>
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia
                      variant="icon"
                      className="text-muted size-20 bg-white"
                    >
                      <Inbox className="size-16" />
                    </EmptyMedia>
                    <EmptyTitle>{t("index.empty_list_title")}</EmptyTitle>
                    <EmptyDescription>
                      {t("index.empty_list_description")}
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              </CardContent>
            </Card>
          ) : (
            live_classes.map((liveClass, index) => (
              <LiveClassCard liveClass={liveClass} key={index} />
            ))
          )}
        </ul>
      </PageBody>
    </>
  )
}

function LiveClassCard({
  liveClass,
}: {
  liveClass: IndexProps["live_classes"][number]
}) {
  const { t } = useTranslation("class_membership_requests", {
    keyPrefix: "index",
  })
  const { t: tState } = useTranslation("class_membership_requests", {
    keyPrefix: "state",
  })
  const titleId = useId()
  return (
    <Card>
      <CardHeader>
        <CardTitle id={titleId}>
          {t("priority_title", {
            rank: liveClass.priority_rank,
            instance: liveClass.instance_index + 1,
          })}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <ul className="grid w-full grid-cols-2 gap-4" aria-labelledby={titleId}>
          {liveClass.requests.map((request) => (
            <Card
              key={request.id}
              role="listitem"
              aria-labelledby={`${titleId}-${request.id}`}
            >
              <CardHeader>
                <CardTitle id={`${titleId}-${request.id}`}>
                  {request.student_name}
                </CardTitle>
                <CardDescription>{request.school_name}</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-row flex-wrap gap-6">
                <dl className="grid w-fit grid-cols-2 gap-2">
                  <dt>{t("state")}</dt>
                  <dd data-testid="state" className="font-semibold">
                    {tState(request.state)}
                  </dd>
                  <dt>{t("expires_at")}</dt>
                  <dd className="font-semibold" data-testid="expires_at">
                    {Temporal.Instant.from(request.expires_at).toLocaleString(
                      undefined,
                    )}
                  </dd>
                </dl>
              </CardContent>
              <CardFooter className="flex flex-row flex-wrap gap-6">
                <Button asChild>
                  <Link
                    method="patch"
                    href={`./transfers/${request.id}`}
                    data={{ response: "accept" }}
                    className="grow"
                  >
                    {t("accept")}
                  </Link>
                </Button>
                <Button asChild>
                  <Link
                    method="patch"
                    href={`./transfers/${request.id}`}
                    data={{ response: "reject" }}
                    className="grow"
                  >
                    {t("reject")}
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}
